import React from 'react';
import { ArrowLeft, Cookie, Settings, BarChart3, Target, Shield, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';

interface CookiePolicyPageProps {
  onNavigate: (page: string) => void;
}

export function CookiePolicyPage({ onNavigate }: CookiePolicyPageProps) {
  const cookieTypes = [
    {
      id: 'essenciais',
      title: 'Cookies Essenciais',
      icon: Shield,
      required: true,
      description: 'Necessários para o funcionamento básico da plataforma',
      examples: [
        'Autenticação e segurança da sessão',
        'Carrinho de compras e preferências temporárias',
        'Configurações de idioma e região',
        'Prevenção de fraudes e ataques'
      ],
      duration: 'Durante a sessão ou até 1 ano'
    },
    {
      id: 'performance',
      title: 'Cookies de Performance',
      icon: BarChart3,
      required: false,
      description: 'Ajudam a melhorar a experiência do usuário',
      examples: [
        'Análise de performance das páginas',
        'Tempos de carregamento e erros',
        'Caminhos de navegação mais populares',
        'Otimização da velocidade do site'
      ],
      duration: 'Até 2 anos'
    },
    {
      id: 'funcionais',
      title: 'Cookies Funcionais',
      icon: Settings,
      required: false,
      description: 'Personalizam a experiência baseada em suas preferências',
      examples: [
        'Lembrar suas preferências de eventos',
        'Configurações de tema (claro/escuro)',
        'Localização para eventos próximos',
        'Filtros e ordenação personalizados'
      ],
      duration: 'Até 1 ano'
    },
    {
      id: 'marketing',
      title: 'Cookies de Marketing',
      icon: Target,
      required: false,
      description: 'Utilizados para campanhas publicitárias relevantes',
      examples: [
        'Anúncios personalizados baseados no interesse',
        'Remarketing de eventos visitados',
        'Análise de efetividade de campanhas',
        'Integração com redes sociais'
      ],
      duration: 'Até 1 ano'
    }
  ];

  const thirdPartyServices = [
    {
      name: 'Google Analytics',
      purpose: 'Análise de tráfego e comportamento do usuário',
      cookies: '_ga, _gid, _gat',
      privacy: 'https://policies.google.com/privacy'
    },
    {
      name: 'Facebook Pixel',
      purpose: 'Remarketing e análise de conversões',
      cookies: '_fbp, _fbc',
      privacy: 'https://www.facebook.com/privacy/explanation'
    },
    {
      name: 'Hotjar',
      purpose: 'Mapas de calor e gravações de sessão',
      cookies: '_hjid, _hjSession',
      privacy: 'https://www.hotjar.com/legal/policies/privacy/'
    },
    {
      name: 'Stripe',
      purpose: 'Processamento seguro de pagamentos',
      cookies: '__stripe_mid, __stripe_sid',
      privacy: 'https://stripe.com/privacy'
    }
  ];

  return (
    <div className="min-h-screen pt-18 bg-gradient-to-br from-background via-muted/20 to-orange-primary/5">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => onNavigate('home')}
            className="mb-6 text-muted-foreground hover:text-foreground transition-all duration-200 hover:translate-x-1 group"
          >
            <ArrowLeft size={20} className="mr-2 transition-transform duration-200 group-hover:-translate-x-1" />
            Voltar ao início
          </Button>

          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-orange-primary/10 rounded-full flex items-center justify-center">
                <Cookie className="w-8 h-8 text-orange-primary" />
              </div>
            </div>
            <h1 className="text-3xl lg:text-4xl font-heading mb-4">
              Política de Cookies
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Entenda como utilizamos cookies para melhorar sua experiência e personalizar nossos serviços.
            </p>
          </div>
        </div>

        {/* What are Cookies */}
        <Card className="bg-card/50 backdrop-blur-sm border-border/50 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center space-x-3">
              <Cookie className="w-6 h-6 text-orange-primary" />
              <span>O que são Cookies?</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Cookies são pequenos arquivos de texto armazenados no seu navegador quando você visita um site. 
              Eles permitem que o site reconheça seu dispositivo e lembre suas preferências, tornando sua experiência mais eficiente e personalizada.
            </p>
            <div className="bg-orange-primary/5 border border-orange-primary/20 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                <strong>Importante:</strong> Os cookies não contêm informações pessoais identificáveis e não podem executar programas ou infectar seu computador com vírus.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cookie Types */}
        <div className="space-y-6 mb-8">
          <h2 className="text-2xl font-heading text-center mb-6">Tipos de Cookies Utilizados</h2>
          
          {cookieTypes.map((type, index) => (
            <Card key={type.id} className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-primary/10 rounded-lg flex items-center justify-center">
                      <type.icon className="w-5 h-5 text-orange-primary" />
                    </div>
                    <span>{type.title}</span>
                  </CardTitle>
                  <div className="flex items-center space-x-2">
                    {type.required ? (
                      <span className="text-sm text-muted-foreground">Obrigatório</span>
                    ) : (
                      <Switch disabled />
                    )}
                  </div>
                </div>
                <CardDescription>{type.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Exemplos de uso:</h4>
                  <ul className="space-y-2">
                    {type.examples.map((example, exampleIndex) => (
                      <li key={exampleIndex} className="flex items-start space-x-3">
                        <div className="w-1.5 h-1.5 bg-orange-primary rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-muted-foreground">{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-sm text-muted-foreground">
                    <strong>Duração:</strong> {type.duration}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Third Party Services */}
        <Card className="bg-card/50 backdrop-blur-sm border-border/50 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center space-x-3">
              <RefreshCw className="w-6 h-6 text-orange-primary" />
              <span>Serviços de Terceiros</span>
            </CardTitle>
            <CardDescription>
              Utilizamos alguns serviços de terceiros que também podem definir cookies
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {thirdPartyServices.map((service, index) => (
                <div key={index} className="border border-border/50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold">{service.name}</h4>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => window.open(service.privacy, '_blank')}
                    >
                      Ver Política
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{service.purpose}</p>
                  <p className="text-xs text-muted-foreground">
                    <strong>Cookies:</strong> {service.cookies}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cookie Management */}
        <Card className="bg-card/50 backdrop-blur-sm border-border/50 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center space-x-3">
              <Settings className="w-6 h-6 text-orange-primary" />
              <span>Gerenciar Cookies</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-semibold mb-3">Configurações do Navegador</h4>
              <p className="text-muted-foreground mb-4">
                Você pode gerenciar cookies diretamente nas configurações do seu navegador:
              </p>
              <ul className="space-y-2">
                {[
                  'Chrome: Configurações > Privacidade e segurança > Cookies',
                  'Firefox: Preferências > Privacidade e segurança > Cookies',
                  'Safari: Preferências > Privacidade > Gerenciar dados do site',
                  'Edge: Configurações > Privacidade > Cookies'
                ].map((browser, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-orange-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-muted-foreground">{browser}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Separator />

            <div>
              <h4 className="font-semibold mb-3">Consequências da Desativação</h4>
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Atenção:</strong> Desativar cookies pode afetar o funcionamento de algumas funcionalidades do site, 
                  como login automático, preferências salvas e carrinho de compras.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="bg-gradient-to-r from-orange-primary/5 to-orange-primary/10 border-orange-primary/20">
          <CardContent className="p-8 text-center">
            <Cookie className="w-8 h-8 text-orange-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-4">
              Dúvidas sobre Cookies?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Se você tiver dúvidas sobre como utilizamos cookies ou quiser exercer seus direitos 
              de privacidade, entre em contato conosco.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.open('mailto:privacidade@uniqueingressos.com.br', '_blank')}
                className="bg-orange-primary hover:bg-orange-primary/90 text-white"
              >
                <Cookie className="w-4 h-4 mr-2" />
                Contatar sobre Cookies
              </Button>
              <Button 
                variant="outline" 
                onClick={() => onNavigate('politica-privacidade')}
              >
                Ver Política de Privacidade
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            Última atualização: 8 de junho de 2025 • Unique Eventos e Entretenimento Ltda.
          </p>
        </div>
      </div>
    </div>
  );
}